import type {
  CitationJudgement,
  QualityItem,
  QualityQuestionKind,
  QualityRun,
  QualityVerdict,
} from '@buakei/types';
import { Badge, Button, EmptyState, ErrorState, LoadingState } from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useId, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { IdentityClient } from '@/auth/identity-client';
import { AnswerView } from '@/assistant/ActsAssistant';
import { useAuth } from '@/auth/session';
import {
  DraftKeyPoints,
  gradedCorrect,
  KeyPointsSection,
  useKeyPoints,
} from '@/pages/editorial/KeyPoints';
import { useReaderTokens } from '@/pages/library/LibraryPages';
import { qualityApi } from '@/services/api';
import { Page } from '@/shell/Page';

/**
 * Legal quality review (ADR-032): lawyers grade the system's answers to a fixed question set,
 * and a legal reviewer signs off the run as the record for the release gates.
 */

export const VERDICT_LABEL: Record<QualityVerdict, string> = {
  correct: 'Correct',
  partly_correct: 'Partly correct or incomplete',
  incorrect: 'Incorrect',
  correctly_declined: 'Correctly said it was not found',
  should_have_answered: 'Should have answered (the library holds it)',
};
const CITATION_LABEL: Record<CitationJudgement, string> = {
  all_support: 'Every citation supports its statement',
  some_unsupported: 'Some citations do not support their statement',
  none_support: 'The citations do not support the answer',
  no_citations: 'No citations (as expected for "not found")',
};
const KIND_LABEL: Record<QualityQuestionKind, string> = {
  answerable: 'Answerable',
  not_in_library: 'Not in the library',
  conflicting: 'Conflicting authorities',
  amendment: 'Amended or repealed law',
  injection: 'Prompt-injection attempt',
};
const verdictTone = (v: QualityVerdict) =>
  v === 'incorrect' || v === 'should_have_answered'
    ? 'danger'
    : v === 'partly_correct'
      ? 'plain'
      : 'success';

const keys = {
  overview: ['quality'] as const,
  run: (id: string) => ['quality', 'run', id] as const,
};

function useEditorialTokens() {
  const { session } = useAuth();
  const tokens = useReaderTokens();
  const editorial = Boolean(session?.experiences.includes('editorial'));
  return editorial ? tokens : null;
}

export function QualityPage() {
  const tokens = useEditorialTokens();
  const { session } = useAuth();
  const navigate = useNavigate();
  const overview = useQuery({
    queryKey: keys.overview,
    queryFn: () => qualityApi.overview(tokens!),
    enabled: tokens !== null,
  });
  const start = useMutation({
    mutationFn: (setId: string) => qualityApi.createRun(tokens!, setId),
    onSuccess: ({ id }) => void navigate(`/editorial/quality/${encodeURIComponent(id)}`),
  });
  if (!tokens) {
    return (
      <Page title="Quality review">
        <EmptyState title="Quality review is for the Editorial console" />
      </Page>
    );
  }
  return (
    <Page
      title="Quality review"
      description="Lawyers grade the system's answers to a fixed set of questions. A legal reviewer signs off each run; signed-off runs are the record for release decisions."
      width="wide"
    >
      {overview.isPending ? <LoadingState label="Loading quality review" /> : null}
      {overview.isError ? (
        <ErrorState
          title="Quality review could not be loaded"
          onRetry={() => void overview.refetch()}
        >
          {overview.error.message}
        </ErrorState>
      ) : null}
      {overview.data ? (
        <>
          <section aria-labelledby="runs-h" className="flex flex-col gap-3">
            <h2 id="runs-h" className="font-serif text-xl font-semibold text-navy-900">
              Review runs
            </h2>
            {overview.data.runs.length === 0 ? (
              <p className="text-sm text-muted-600">No runs yet.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line-200 rounded-lg border border-line-200 bg-white">
                {overview.data.runs.map((r) => (
                  <li key={r.id} className="flex flex-col gap-1 p-4">
                    <Link
                      to={`/editorial/quality/${encodeURIComponent(r.id)}`}
                      className="font-medium text-navy-900 underline-offset-2 hover:underline"
                    >
                      {r.title}
                    </Link>
                    <span className="flex flex-wrap items-center gap-2 text-sm text-muted-600">
                      {r.signOff ? (
                        <Badge tone="success">
                          Signed off by {r.signOff.byName ?? 'a reviewer'}
                        </Badge>
                      ) : (
                        <Badge>In progress</Badge>
                      )}
                      {r.summary.answered} of {r.summary.total} answered · {r.summary.graded} graded
                      · model {r.model}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section aria-labelledby="sets-h" className="flex flex-col gap-3">
            <h2 id="sets-h" className="font-serif text-xl font-semibold text-navy-900">
              Question sets
            </h2>
            {overview.data.sets.length === 0 ? (
              <p className="text-sm text-muted-600">No question sets yet.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-line-200 rounded-lg border border-line-200 bg-white">
                {overview.data.sets.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                    <span className="flex flex-col">
                      <span className="font-medium text-navy-900">{s.title}</span>
                      <span className="text-sm text-muted-600">
                        {s.questions.length} questions ·{' '}
                        {[...new Set(s.questions.map((q) => q.area))].join(', ')}
                      </span>
                    </span>
                    {overview.data.canManage ? (
                      <Button
                        size="sm"
                        disabled={start.isPending}
                        onClick={() => start.mutate(s.id)}
                      >
                        Start a review run
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {start.isError ? (
              <p role="alert" className="text-sm text-ink-900">
                {start.error.message}
              </p>
            ) : null}
          </section>
          {session ? <KeyPointsSection tokens={tokens} myUid={session.uid} /> : null}
        </>
      ) : null}
    </Page>
  );
}

function GradeForm({
  tokens,
  run,
  item,
  myUid,
}: {
  tokens: IdentityClient;
  run: QualityRun;
  item: QualityItem;
  myUid: string;
}) {
  const queryClient = useQueryClient();
  const mine = item.grades.find((g) => g.reviewerUid === myUid);
  const ids = { verdict: useId(), citations: useId(), notes: useId() };
  const [verdict, setVerdict] = useState<QualityVerdict>(mine?.verdict ?? 'correct');
  const [citations, setCitations] = useState<CitationJudgement>(mine?.citations ?? 'all_support');
  const [notes, setNotes] = useState(mine?.notes ?? '');
  const save = useMutation({
    mutationFn: () =>
      qualityApi.grade(tokens, run.id, item.question.id, { verdict, citations, notes }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.run(run.id) }),
  });
  const select = 'h-10 w-full rounded-md border border-line-200 bg-white px-2 text-ink-900';
  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-line-200 bg-soft-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h4 className="text-sm font-semibold text-navy-900">
        {mine ? 'Your grade' : 'Grade this answer'}
      </h4>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.verdict} className="text-sm font-medium text-ink-900">
            The answer is
          </label>
          <select
            id={ids.verdict}
            value={verdict}
            onChange={(e) => setVerdict(e.target.value as QualityVerdict)}
            className={select}
          >
            {(Object.keys(VERDICT_LABEL) as QualityVerdict[]).map((v) => (
              <option key={v} value={v}>
                {VERDICT_LABEL[v]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.citations} className="text-sm font-medium text-ink-900">
            Citations
          </label>
          <select
            id={ids.citations}
            value={citations}
            onChange={(e) => setCitations(e.target.value as CitationJudgement)}
            className={select}
          >
            {(Object.keys(CITATION_LABEL) as CitationJudgement[]).map((c) => (
              <option key={c} value={c}>
                {CITATION_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={ids.notes} className="text-sm font-medium text-ink-900">
          Notes
        </label>
        <textarea
          id={ids.notes}
          rows={3}
          maxLength={4000}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What is wrong or missing, and the authority that shows it"
          className="rounded-md border border-line-200 bg-white p-2 text-ink-900 placeholder:text-muted-600"
        />
      </div>
      <Button type="submit" size="sm" className="self-start" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : mine ? 'Update my grade' : 'Save my grade'}
      </Button>
      {save.isSuccess ? (
        <p role="status" className="text-sm text-ink-900">
          Grade saved.
        </p>
      ) : null}
      {save.isError ? (
        <p role="alert" className="text-sm text-ink-900">
          {save.error.message}
        </p>
      ) : null}
    </form>
  );
}

function ItemCard({
  tokens,
  run,
  item,
  myUid,
  index,
  canDraft,
}: {
  tokens: IdentityClient;
  run: QualityRun;
  item: QualityItem;
  myUid: string;
  index: number;
  /** May draft key points from an answer graded correct (ADR-035). */
  canDraft: boolean;
}) {
  return (
    <article
      aria-labelledby={`q-${item.question.id}`}
      className="flex flex-col gap-4 rounded-lg border border-line-200 bg-white p-4 md:p-6"
    >
      <header className="flex flex-col gap-2">
        <span className="flex flex-wrap items-center gap-2 text-xs text-muted-600">
          <span className="font-semibold text-navy-900 tabular-nums">Q{index + 1}</span>
          <span>{item.question.area}</span>
          <Badge>{KIND_LABEL[item.question.kind]}</Badge>
          {item.status === 'pending' ? <Badge>Not generated</Badge> : null}
          {item.status === 'failed' ? <Badge tone="danger">Generation failed</Badge> : null}
        </span>
        <h3 id={`q-${item.question.id}`} className="font-serif text-lg font-semibold text-navy-900">
          {item.question.question}
        </h3>
        {item.question.guidance ? (
          <p className="rounded-md bg-navy-50 px-3 py-2 text-sm text-ink-900">
            <strong className="text-navy-900">For graders: </strong>
            {item.question.guidance}
          </p>
        ) : null}
      </header>
      {item.answer ? <AnswerView answer={item.answer} turnId={index + 1} /> : null}
      {item.grades.length > 0 ? (
        <section aria-label="Grades" className="flex flex-col gap-2">
          <h4 className="text-sm font-semibold text-navy-900">Grades</h4>
          <ul className="flex flex-col gap-2">
            {item.grades.map((g) => (
              <li key={g.reviewerUid} className="flex flex-col gap-1 text-sm">
                <span className="flex flex-wrap items-center gap-2">
                  <Badge tone={verdictTone(g.verdict)}>{VERDICT_LABEL[g.verdict]}</Badge>
                  <span className="text-muted-600">
                    {CITATION_LABEL[g.citations]} · {g.reviewerName ?? 'A reviewer'}
                  </span>
                </span>
                {g.notes ? <p className="whitespace-pre-wrap text-ink-900">{g.notes}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {run.canGrade && item.status === 'answered' ? (
        <GradeForm tokens={tokens} run={run} item={item} myUid={myUid} />
      ) : null}
      {canDraft && item.answer?.outcome === 'answered' && gradedCorrect(item) ? (
        <DraftKeyPoints tokens={tokens} run={run} item={item} />
      ) : null}
    </article>
  );
}

function Generator({ tokens, run }: { tokens: IdentityClient; run: QualityRun }) {
  const queryClient = useQueryClient();
  const pending = run.items.filter((i) => i.status !== 'answered');
  const [progress, setProgress] = useState<{ done: number; failed: number } | null>(null);
  const [running, setRunning] = useState(false);
  if (!run.canManage || pending.length === 0) return null;
  const go = async () => {
    setRunning(true);
    let done = 0;
    let failed = 0;
    setProgress({ done, failed });
    // One at a time: the server allows one AI request in flight per account (ADR-027).
    for (const item of pending) {
      try {
        await qualityApi.generate(tokens, run.id, item.question.id);
        done += 1;
      } catch {
        failed += 1;
      }
      setProgress({ done, failed });
      await queryClient.invalidateQueries({ queryKey: keys.run(run.id) });
    }
    setRunning(false);
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line-200 bg-white p-4">
      <p className="text-sm text-ink-900">
        {pending.length} question{pending.length === 1 ? '' : 's'} without an answer. Generating
        uses the same research as Ask Buakei and counts against the month's AI budget (about
        US$0.05–0.10 each).
      </p>
      <Button className="self-start" disabled={running} onClick={() => void go()}>
        {running
          ? 'Generating…'
          : `Generate ${pending.length} answer${pending.length === 1 ? '' : 's'}`}
      </Button>
      {progress ? (
        <p role="status" aria-live="polite" className="text-sm text-ink-900">
          {progress.done} generated
          {progress.failed ? `, ${progress.failed} failed (try again)` : ''}.
        </p>
      ) : null}
    </div>
  );
}

function SignOff({ tokens, run }: { tokens: IdentityClient; run: QualityRun }) {
  const queryClient = useQueryClient();
  const statementId = useId();
  const [statement, setStatement] = useState('');
  const sign = useMutation({
    mutationFn: () => qualityApi.signOff(tokens, run.id, statement),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.run(run.id) });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
    },
  });
  const s = run.summary;
  const ready = s.answered === s.total && s.graded === s.total;
  return (
    <section
      aria-labelledby="signoff-h"
      className="flex flex-col gap-3 rounded-lg border border-line-200 bg-white p-4 md:p-6"
    >
      <h2 id="signoff-h" className="font-serif text-xl font-semibold text-navy-900">
        Summary and sign-off
      </h2>
      <p className="text-sm text-ink-900 tabular-nums">
        {s.total} questions · {s.answered} answered · {s.graded} graded ·{' '}
        {(Object.entries(s.verdicts) as [QualityVerdict, number][])
          .map(([v, n]) => `${n} ${VERDICT_LABEL[v].toLowerCase()}`)
          .join(' · ') || 'no grades yet'}{' '}
        · citations fully supported in {s.citationsAllSupport}
      </p>
      {run.signOff ? (
        <div className="flex flex-col gap-1 text-sm text-ink-900">
          <p>
            <Badge tone="success">Signed off</Badge> by {run.signOff.byName ?? 'a reviewer'} on{' '}
            {new Date(run.signOff.at).toLocaleString('en-GB')}. The run can no longer change.
          </p>
          <p className="whitespace-pre-wrap">{run.signOff.statement}</p>
        </div>
      ) : run.canManage ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            sign.mutate();
          }}
        >
          <label htmlFor={statementId} className="text-sm font-medium text-ink-900">
            Sign-off statement
          </label>
          <textarea
            id={statementId}
            rows={4}
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            placeholder="What was reviewed, the overall result, known failures and any conditions for release"
            className="rounded-md border border-line-200 bg-white p-2 text-ink-900 placeholder:text-muted-600"
          />
          {!ready ? (
            <p className="text-sm text-muted-600">
              Every question needs an answer and at least one grade first.
            </p>
          ) : null}
          <Button
            type="submit"
            className="self-start"
            disabled={!ready || sign.isPending || statement.trim().length < 20}
          >
            Sign off this run
          </Button>
          {sign.isError ? (
            <p role="alert" className="text-sm text-ink-900">
              {sign.error.message}
            </p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}

export function QualityRunPage() {
  const tokens = useEditorialTokens();
  const { session } = useAuth();
  const { runId = '' } = useParams();
  const run = useQuery({
    queryKey: keys.run(runId),
    queryFn: () => qualityApi.run(tokens!, runId),
    enabled: tokens !== null,
  });
  const keyPoints = useKeyPoints(tokens);
  if (!tokens || !session) {
    return (
      <Page title="Quality review">
        <EmptyState title="Quality review is for the Editorial console" />
      </Page>
    );
  }
  return (
    <Page title={run.data?.title ?? 'Review run'} width="wide">
      <Link
        to="/editorial/quality"
        className="flex items-center gap-1 self-start text-sm font-medium text-navy-900 underline"
      >
        <ArrowLeft className="size-4" aria-hidden /> All review runs
      </Link>
      {run.isPending ? <LoadingState label="Loading the run" /> : null}
      {run.isError ? (
        <ErrorState title="This run could not be loaded" onRetry={() => void run.refetch()}>
          {run.error.message}
        </ErrorState>
      ) : null}
      {run.data ? (
        <div className="flex flex-col gap-6">
          <SignOff tokens={tokens} run={run.data} />
          <Generator tokens={tokens} run={run.data} />
          {run.data.items.map((item, i) => (
            <ItemCard
              key={item.question.id}
              tokens={tokens}
              run={run.data}
              item={item}
              myUid={session.uid}
              index={i}
              canDraft={keyPoints.data?.canDraft ?? false}
            />
          ))}
        </div>
      ) : null}
    </Page>
  );
}
